import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import { Banker } from "../../../validation/admin.validation";

interface Props {
  bankers: Banker[];
  loading: boolean;
  handleViewDetail: (banker: Banker) => void;
}

const BankerCardView: React.FC<Props> = ({ bankers, loading, handleViewDetail }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 lg:mb-8">
      {loading ? (
        <div className="text-center py-12 col-span-full">
          <div className="animate-pulse text-gray-500">Loading bankers...</div>
        </div>
      ) : bankers.length === 0 ? (
        <div className="text-center py-12 col-span-full">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Bankers Found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria.</p>
        </div>
      ) : (
        bankers.map((banker) => (
          <div key={banker.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">{`${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`.trim()}</h3>
                </div>
                <div className="ml-2 sm:ml-3 flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <PersonIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3 md:space-y-3 lg:space-y-3">
                <div className="flex items-center text-xs sm:text-sm md:text-sm lg:text-sm">
                  <EmailIcon className="w-3 h-3 sm:w-4 md:w-4 lg:w-4 h-3 sm:h-4 md:h-4 lg:h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600 mr-1">Email:</span>
                  <span className="font-medium text-gray-900 break-all text-xs sm:text-sm md:text-sm lg:text-sm">{banker.email}</span>
                </div>

                <div className="flex items-center text-xs sm:text-sm md:text-sm lg:text-sm">
                  <PhoneIcon className="w-3 h-3 sm:w-4 md:w-4 lg:w-4 h-3 sm:h-4 md:h-4 lg:h-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600 mr-1">Phone:</span>
                  <span className="font-medium text-gray-900 truncate">{banker.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>

            <div className="px-4 pb-4">
              <Tooltip title="View detailed information about this banker">
                <IconButton
                  onClick={() => handleViewDetail(banker)}
                  className="w-full"
                  sx={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    borderRadius: '0.375rem',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#1d4ed8',
                    },
                    '&:focus': {
                      outline: 'none',
                      boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.5)',
                    },
                  }}
                >
                  <Visibility className="mr-2 w-4 h-4" />
                  View Details
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default BankerCardView;